<template>
  <pf-alert-group toast>
    <pf-alert
      v-for="message in alertStore.alerts.filter(m => m.toast)"
      :key="message.id"
      :variant="message.type"
      :title="message.title"
      :timeout="message.persistent ? false : (message.delay || 4000)"
      live-region
      @timeout="alertStore.update({ id: message.id, toast: false })"
      @close="alertStore.update({ id: message.id, toast: false })"
    >
      <template v-if="message.actions?.length" #action-links>
        <pf-alert-action-link v-for="action of message.actions" :key="action.name" @click="action.handler">{{ action.name }}</pf-alert-action-link>
      </template>
      <div style="white-space: pre">{{ message.description }}</div>
    </pf-alert>
  </pf-alert-group>

  <pf-flex direction="column" flex-wrap="nowrap" space-items="none" class="pf-v6-u-h-100vh" @click="outsideClick">
    <template v-if="loggedIn && !app.isInIframe && login.contratto_cliente && login.approval === 'approvato'">
      <pf-banner v-if="!login.complete" status="warning">
        <div class="pf-v6-u-display-none-on-lg pf-v6-u-text-align-center">
          <granted-link :to="{ name: 'company' }">{{ $t('Dati di fatturazione incompleti') }}</granted-link>
        </div>
        <div class="pf-v6-u-display-none pf-v6-u-display-block-on-lg pf-m-justify-content-center">
          <strong>{{ $t('Attenzione!') }}</strong>
          {{ $t('I dati di fatturazione sono incompleti e vanno compilati per poter ricevere le fatture.') }}
          <granted-link :to="{ name: 'company' }">{{ $t('Modifica') }}</granted-link>
        </div>
      </pf-banner>

      <pf-banner v-else-if="!login.compiledSurvey" status="info">
        <div class="pf-v6-u-display-none-on-lg pf-v6-u-text-align-center">
          <pf-button inline variant="link" @click="showSurveyModal = true">{{ $t('Compila il questionario annuale') }}</pf-button>
        </div>
        <div class="pf-v6-u-display-none pf-v6-u-display-block-on-lg">
          {{ $t('Compila il questionario annuale e sblocca tutti i vantaggi.') }}
          <pf-button inline variant="link" @click="showSurveyModal = true">{{ $t('Clicca qui per compilarlo') }}</pf-button>
        </div>
      </pf-banner>
    </template>

    <pf-flex-item v-if="app.maintenance" grow style="min-height: 0">
      <maintenance-page />
    </pf-flex-item>

    <pf-flex-item v-else-if="loggedIn && !$route.meta.fullPageWithBg" grow style="min-height: 0">
      <pf-page ref="page" @page-resize="mobileView = $event.mobileView">
        <template #skeleton>
          <pf-masthead style="--pf-v6-c-masthead--GridTemplateColumns: auto 1fr">
            <pf-masthead-main>
              <pf-masthead-toggle ref="navToggleRef" class="pf-v6-u-display-none-on-xl" @click.stop="navSidebarOpen = !navSidebarOpen">
                <pf-page-toggle-button>
                  <x-icon v-if="navSidebarOpen" />
                  <bars-icon v-else />
                </pf-page-toggle-button>
              </pf-masthead-toggle>
              <pf-masthead-brand :to="{ name: 'dashboard' }">
                <img src="@/assets/logo.svg" width="32" height="32" alt="Artera">
              </pf-masthead-brand>
              <div class="masthead-main-title">
                <div>{{ $t('Area riservata') }}</div>
                <div class="masthead-main-customer-name">{{ login.azienda }}</div>
              </div>
            </pf-masthead-main>
            <pf-masthead-content>
              <pf-toolbar full-height static>
                <pf-toolbar-content>
                  <pf-toolbar-group align="end" class="pf-v6-u-display-none pf-v6-u-display-flex-on-sm">
                    <pf-toolbar-item>
                      <pf-toggle-group>
                        <pf-toggle-group-item :selected="!app.darkTheme" @click="app.darkTheme = false">
                          <sun-icon />
                        </pf-toggle-group-item>
                        <pf-toggle-group-item :selected="app.darkTheme" @click="app.darkTheme = true">
                          <moon-icon />
                        </pf-toggle-group-item>
                      </pf-toggle-group>
                    </pf-toolbar-item>

                    <pf-toolbar-item data-ouia-component-id="user-menu-toolbar-item">
                      <pf-dropdown position="right">
                        <template #toggle>
                          <pf-menu-toggle full-height>
                            <template #icon>
                              <user-icon />
                            </template>
                            {{ login.username }}
                          </pf-menu-toggle>
                        </template>

                        <pf-dropdown-list>
                          <template v-if="!route.meta.disableNavigation">
                            <granted-link type="dropdown-item" :to="{ name: 'profile' }" data-ouia-component-id="profile">
                              {{ $t('Modifica profilo') }}
                            </granted-link>
                            <granted-link type="dropdown-item" :to="{ name: 'company' }" data-ouia-component-id="company">
                              {{ $t('Dati di fatturazione') }}
                            </granted-link>
                            <granted-link type="dropdown-item" :to="{ name: 'users' }" data-ouia-component-id="users">
                              {{ $t('Utenze') }}
                            </granted-link>
                          </template>
                          <granted-link v-if="aziendeCount > 1" type="dropdown-item" :to="{ name: 'login_azienda' }" data-ouia-component-id="change_company">
                            {{ $t('Cambia Azienda') }}
                          </granted-link>
                          <granted-link type="dropdown-item" :to="{ name: 'webmail' }" target="_blank" data-ouia-component-id="webmail">
                            {{ $t('Webmail') }}
                          </granted-link>
                          <pf-dropdown-item @click="app.logout()" data-ouia-component-id="logout">
                            {{ $t('Esci') }}
                          </pf-dropdown-item>
                        </pf-dropdown-list>
                      </pf-dropdown>
                    </pf-toolbar-item>
                  </pf-toolbar-group>
                </pf-toolbar-content>
              </pf-toolbar>
            </pf-masthead-content>
          </pf-masthead>

          <pf-page-sidebar ref="sidebarRef" :sidebar-open="navSidebarOpen">
            <pf-nav @click="clickNav">
              <nav-group :title="$t('Codice cliente: {0}', [login.id_azienda])">
                <granted-link type="nav-item" :to="{ name: 'dashboard' }" ouia-id="dashboard">
                  <template #icon><house-icon /></template>
                  {{ $t('Dashboard') }}
                </granted-link>

                <granted-link type="nav-item" :to="{ name: 'catalog' }" ouia-id="catalog">
                  <template #icon><cart-shopping-icon /></template>
                  {{ $t('Listino prezzi') }}
                </granted-link>

                <granted-link type="nav-item" :to="{ name: 'diy-offer' }" ouia-id="diy-offer">
                  <template #icon><wand-magic-icon /></template>
                  {{ $t('Offerta fai da te') }}
                </granted-link>

                <granted-link type="nav-item" :to="{ name: 'offers' }" ouia-id="offers">
                  <template #icon><file-lines-icon /></template>
                  {{ $t('Offerte e ordini') }}
                </granted-link>

                <granted-link type="nav-item" :to="{ name: 'projects' }" ouia-id="projects">
                  <template #icon><pen-ruler-icon /></template>
                  {{ $t('Presenta un progetto') }}
                </granted-link>

                <granted-link type="nav-item" :to="{ name: 'become_partner' }" ouia-id="become_partner">
                  <template #icon><handshake-icon /></template>
                  {{ $t('Diventa partner') }}
                </granted-link>
              </nav-group>

              <nav-group :title="$t('Servizi')">
                <granted-link type="nav-item" :to="{ name: 'services' }" ouia-id="services">
                  <template #icon><server-icon /></template>
                  {{ $t('My hosting') }}
                </granted-link>

                <granted-link type="nav-item" :to="{ name: 'servers' }" ouia-id="servers">
                  <template #icon><cloud-icon /></template>
                  {{ $t('My cloud') }}
                </granted-link>

                <granted-link type="nav-item" :to="{ name: 'products' }" ouia-id="products">
                  <template #icon><layer-group-icon /></template>
                  {{ $t('I miei prodotti') }}
                </granted-link>

                <granted-link type="nav-item" :to="{ name: 'transfers' }" ouia-id="transfers">
                  <template #icon><arrows-to-circle-icon /></template>
                  {{ $t('Trasferimenti') }}
                </granted-link>
              </nav-group>

              <nav-group :title="$t('Amministrazione')" ouia-id="NavGroup-admin">
                <granted-link type="nav-item" :to="{ name: 'invoices' }" ouia-id="invoices">
                  <template #icon><file-invoice-dollar-icon /></template>
                  {{ $t('Fatture') }}
                </granted-link>

                <granted-link type="nav-item" :to="{ name: 'account_movements' }" ouia-id="account_movements">
                  <template #icon><wallet-icon /></template>
                  {{ $t('Conto Artera') }}
                </granted-link>

                <granted-link type="nav-item" :to="{ name: 'documents' }" ouia-id="documents">
                  <template #icon><file-signature-icon /></template>
                  {{ $t('Documenti utili') }}
                </granted-link>
              </nav-group>

              <nav-group :title="$t('Area partner')" ouia-id="NavGroup-partner-area">
                <granted-link type="nav-item" :to="{ name: 'my_customers' }" ouia-id="my_customers">
                  <template #icon><users-icon /></template>
                  {{ $t('I miei clienti') }}
                </granted-link>

                <granted-link type="nav-item" :to="{name: 'introduction'}" ouia-id="introduction">
                  <template #icon><user-icon /></template>
                  {{ $t('La tua azienda') }}
                </granted-link>

                <granted-link type="nav-item" :to="{name: 'my_skills'}" ouia-id="my_skills">
                  <template #icon><puzzle-piece-icon /></template>
                  {{ $t('Le mie skill') }}
                </granted-link>

                <granted-link type="nav-item" :to="{name: 'opportunities'}" ouia-id="opportunities">
                  <template #icon><thumb-tack-icon /></template>
                  {{ $t('Opportunità') }}
                </granted-link>
              </nav-group>

              <nav-group :title="$t('Assistenza')">
                <granted-link type="nav-item" :to="{ name: 'tickets' }" ouia-id="tickets">
                  <template #icon><ticket-icon /></template>
                  {{ $t('Tickets') }}
                </granted-link>

                <granted-link type="nav-item" :to="{ name: 'security' }" ouia-id="security">
                  <template #icon><unlock-icon /></template>
                  {{ $t('Sblocco IP') }}
                </granted-link>
              </nav-group>

              <nav-group :title="$t('Impostazioni')" class="pf-v6-u-display-none-on-sm">
                <template v-if="!route.meta.disableNavigation">
                  <granted-link type="nav-item" :to="{ name: 'profile' }">
                    <template #icon><user-icon /></template>
                    {{ $t('Modifica profilo') }}
                  </granted-link>
                  <granted-link type="nav-item" :to="{ name: 'company' }">
                    <template #icon><file-invoice-dollar-icon /></template>
                    {{ $t('Dati di fatturazione') }}
                  </granted-link>
                  <granted-link type="nav-item" :to="{ name: 'users' }">
                    <template #icon><users-icon /></template>
                    {{ $t('Utenze') }}
                  </granted-link>
                </template>

                <granted-link v-if="aziendeCount > 1" type="nav-item" :to="{ name: 'login_azienda' }">
                  <template #icon><right-to-bracket /></template>
                  {{ $t('Cambia Azienda') }}
                </granted-link>

                <pf-nav-item @select="app.darkTheme = !app.darkTheme">
                  <template v-if="app.darkTheme" #icon><sun-icon /></template>
                  <template v-else #icon><moon-icon /></template>
                  <template v-if="app.darkTheme">
                    {{ $t('Tema chiaro') }}
                  </template>
                  <template v-else="app.darkTheme">
                    {{ $t('Tema scuro') }}
                  </template>
                </pf-nav-item>

                <pf-nav-item @select="app.logout()">
                  <template #icon>
                    <right-from-bracket-icon />
                  </template>
                  {{ $t('Esci') }}
                </pf-nav-item>
              </nav-group>
            </pf-nav>
          </pf-page-sidebar>
        </template>

        <router-view />
      </pf-page>
    </pf-flex-item>

    <pf-flex-item v-else grow style="min-height: 0">
      <router-view />
    </pf-flex-item>
  </pf-flex>

  <top-progress ref="progress" color="#ffffff" />

  <pf-modal
    v-if="showSurveyModal"
    v-model:open="showSurveyModal"
    :title="$t('Compila il questionario')"
    variant="medium"
  >
    <introduction-form variant="survey" @submit="submittedSurvey" />
  </pf-modal>
</template>

<style scoped>
.pf-v6-c-nav__item{
  --pf-v6-c-nav__link--PaddingBlockStart: 0.15rem;
  --pf-v6-c-nav__link--PaddingBlockEnd: 0.15rem;
}

.masthead-main-title {
  line-height: 1em;
}

.masthead-main-customer-name {
  font-weight: var(--pf-t--global--font--weight--heading--default);
}
</style>

<script lang="ts" setup>
import { ref, type Ref, computed, watch, useTemplateRef } from 'vue';
import { http } from './http';
import MaintenancePage from './pages/maintenance.vue';
import GrantedLink from './components/granted-link.vue';
import { asyncComputed } from '@common/asyncComputed';
import NavGroup from './components/nav-group.vue';
import TopProgress from './components/top-progress.vue';
import { useAppStore } from './store/app';
import { useLoginStore } from './store/login';
import { useAlertStore } from './store/alerts';
import { usePaypalStore } from '@/store/paypal';
import { CompanyAccessResource } from './resources';
import IntroductionForm from './components/introduction-form.vue';
import { useRoute, useRouter } from 'vue-router';
import type { PfPage } from '@vue-patternfly/core';

import UserIcon from '@vue-patternfly/icons/user-icon';
import PuzzlePieceIcon from '@vue-patternfly/icons/puzzle-piece-icon';
import RightToBracket from '@vue-patternfly/icons/right-to-bracket-icon';
import UsersIcon from '@vue-patternfly/icons/users-icon';
import FileLinesIcon from '@vue-patternfly/icons/file-lines-icon';
import HouseIcon from '@vue-patternfly/icons/house-icon';
import FileInvoiceDollarIcon from '@vue-patternfly/icons/file-invoice-dollar-icon';
import FileSignatureIcon from '@vue-patternfly/icons/file-signature-icon';
import UnlockIcon from '@vue-patternfly/icons/unlock-icon';
import ThumbTackIcon from '@vue-patternfly/icons/thumb-tack-icon';
import CloudIcon from '@vue-patternfly/icons/cloud-icon';
import HandshakeIcon from '@vue-patternfly/icons/handshake-icon';
import TicketIcon from '@vue-patternfly/icons/ticket-icon';
import LayerGroupIcon from '@vue-patternfly/icons/layer-group-icon';
import WalletIcon from '@vue-patternfly/icons/wallet-icon';
import CartShoppingIcon from '@vue-patternfly/icons/cart-shopping-icon';
import WandMagicIcon from '@vue-patternfly/icons/wand-magic-icon';
import ServerIcon from '@vue-patternfly/icons/server-icon';
import PenRulerIcon from '@vue-patternfly/icons/pen-ruler-icon';
import BarsIcon from '@vue-patternfly/icons/bars-icon';
import XIcon from '@vue-patternfly/icons/x-icon';
import RightFromBracketIcon from '@vue-patternfly/icons/right-from-bracket-icon';
import SunIcon from "@vue-patternfly/icons/sun-icon";
import MoonIcon from "@vue-patternfly/icons/moon-icon";
import ArrowsToCircleIcon from '@vue-patternfly/icons/arrows-to-circle-icon';
import { useElementVisibility } from '@vueuse/core';

defineOptions({
  name: 'AdminApp',
});

const app = useAppStore();
const login = useLoginStore();
const alertStore = useAlertStore();
const paypal = usePaypalStore();
const progress: Ref<InstanceType<typeof TopProgress> | null> = ref(null);
const mobileView = ref(false);
const page: Ref<InstanceType<typeof PfPage> | undefined> = ref();
const route = useRoute();
const navSidebarOpen = ref(true);
const showSurveyModal = ref(false);
const router = useRouter();

app.router = router;

paypal.load();

http.interceptors.request.use(config => {
  if (!config.params) {
    config.params = {};
  }
  if (route.params.id_cliente) {
    config.params.id_cliente = route.params.id_cliente;
  }
  return config;
});

const sidebar = useTemplateRef('sidebarRef');
const navToggle = useTemplateRef('navToggleRef');
const navToggleVisible = useElementVisibility(navToggle);
watch (navToggleVisible, () => {
  navSidebarOpen.value = !navToggleVisible.value;
}, { immediate: true });

const { result: aziendeCount } = asyncComputed(async() => {
  if (!login.id) {
    return 0;
  }
  const response = await new CompanyAccessResource().get();
  return Object.keys(response).length;
}, 0);

const loggedIn = computed(() => {
  return Boolean(login.id_azienda);
});

watch(() => app.darkTheme, () => {
  if (app.darkTheme) {
    document.documentElement.classList.add('pf-v6-theme-dark');
  } else {
    document.documentElement.classList.remove('pf-v6-theme-dark');
  }
}, { immediate: true });

const bodyClass = computed(() => {
  if (app.maintenance || route.meta.fullPageWithBg) {
    return 'login-pf';
  }
  return '';
});

watch(bodyClass, (to, from) => {
  if (from) {
    document.documentElement.classList.remove(from);
  }
  if (to) {
    document.documentElement.classList.add(to);
  }
});

watch(() => app.loading, (to, from) => {
  if (!progress.value) {
    return;
  }

  if (!to) {
    progress.value.done();
    return;
  } else if (!from) {
    progress.value.start();
    return;
  }

  const perc = progress.value.getProgress();
  progress.value.increase(((100 - perc) / from) * from - to);
}, { immediate: true });

function submittedSurvey() {
  showSurveyModal.value = false;
  login.refresh();
}

function clickNav(e: MouseEvent) {
  if (mobileView.value && e.target instanceof HTMLElement && ['BUTTON', 'A'].includes(e.target.tagName)) {
    page.value?.navToggle();
  }
}

function outsideClick(event: MouseEvent) {
  if (navToggleVisible.value && (!sidebar.value?.$el.contains(event.target) || (event.target instanceof Element && event.target.closest('a, button')))) {
    navSidebarOpen.value = false;
  }
}
</script>
